// ⚡ Performance: State batching with useReducer
// Consolidates multiple useState + useEffect patterns into single update cycle
export interface NodeState {
  prompt: string
  images: string[]
  selectedItemId: string | undefined
  uploadProgress: {
    uploading: boolean
    current: number
    total: number
  }
  removingUrl: string | null
  imageRoles: Record<string, string>
  imageAssets: Record<string, { name?: string; contentType?: string; bytes?: number; cloudUrl?: string }>
}

export type NodeAction =
  | { type: 'SET_PROMPT'; payload: string }
  | { type: 'SET_IMAGES'; payload: string[] }
  | { type: 'ADD_IMAGES'; payload: string[] }
  | { type: 'REMOVE_IMAGE'; payload: string }
  | { type: 'SET_IMAGE_ROLES'; payload: Record<string, string> }
  | { type: 'SET_IMAGE_ASSETS'; payload: NodeState['imageAssets'] }
  | { type: 'SET_SELECTED_ITEM'; payload: string | undefined }
  | { type: 'SET_UPLOAD_PROGRESS'; payload: NodeState['uploadProgress'] }
  | { type: 'SET_REMOVING_URL'; payload: string | null }
  | { type: 'RESET'; payload: { prompt: string; images: string[]; imageRoles?: Record<string, string>; imageAssets?: NodeState['imageAssets'] } }

export function nodeStateReducer(state: NodeState, action: NodeAction): NodeState {
  switch (action.type) {
    case 'SET_PROMPT':
      return { ...state, prompt: action.payload }
    case 'SET_IMAGES':
      return { ...state, images: action.payload }
    case 'ADD_IMAGES':
      return { ...state, images: [...state.images, ...action.payload] }
    case 'REMOVE_IMAGE': {
      const { [action.payload]: _removed, ...imageAssets } = state.imageAssets ?? {}
      const { [action.payload]: _role, ...imageRoles } = state.imageRoles ?? {}
      return { ...state, images: state.images.filter(img => img !== action.payload), imageAssets, imageRoles }
    }
    case 'SET_IMAGE_ROLES':
      return { ...state, imageRoles: action.payload }
    case 'SET_IMAGE_ASSETS':
      return { ...state, imageAssets: action.payload }
    case 'SET_SELECTED_ITEM':
      return { ...state, selectedItemId: action.payload }
    case 'SET_UPLOAD_PROGRESS':
      return { ...state, uploadProgress: action.payload }
    case 'SET_REMOVING_URL':
      return { ...state, removingUrl: action.payload }
    case 'RESET':
      return { ...state, prompt: action.payload.prompt, images: action.payload.images, imageRoles: action.payload.imageRoles ?? {}, imageAssets: action.payload.imageAssets ?? {}, selectedItemId: undefined, removingUrl: null }
    default:
      return state
  }
}
